import React from 'react';
import { motion } from 'framer-motion';
import { cn } from '../../lib/utils';

// ═══════════════════════════════════════════════════════════════════════════════
// CHAT TRANSCRIPT SKELETON
//
// Shown while a session's messages are in flight.
//
// Before this, opening an existing conversation rendered the empty state for a
// beat — a full "How can I help you?" hero with quick prompts — and then
// replaced it with the transcript. That reads as "your chat is gone", which is
// the single most alarming thing a chat app can flash at someone.
//
// So the placeholder is shaped like a conversation instead: alternating user
// and assistant ghosts on the real geometry (right-aligned bubble, left rail).
// Nothing here claims to be the actual content — the widths are fixed and
// arbitrary — it only reserves the right shape so the swap is a fill-in.
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Fixed so the ghost doesn't reflow on re-render, and so it never looks random.
 *
 * User widths stay well under the 60ch cap and the bubbles are one line tall —
 * a placeholder should suggest the lightest plausible content, not the heaviest.
 * Oversized ghosts read as grey slabs and make the swap to real text a visible
 * collapse.
 */
const GHOST_TURNS = [
    { user: 'w-[34%]', lines: ['w-[94%]', 'w-[86%]', 'w-[58%]'] },
    { user: 'w-[48%]', lines: ['w-[90%]', 'w-[72%]'] },
    { user: 'w-[26%]', lines: ['w-[88%]', 'w-[93%]', 'w-[44%]'] },
];

export function ChatTranscriptSkeleton({ reduceMotion = false }) {
    return (
        <motion.div
            initial={reduceMotion ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            // Held back a beat: a cached session usually resolves faster than
            // this, and flashing a skeleton for 80ms is worse than showing
            // nothing at all.
            transition={{ duration: 0.2, delay: reduceMotion ? 0 : 0.18 }}
            // gap-10 between turns, gap-4 inside one: a prompt and its reply are
            // a pair, and spacing them like strangers is what made the first
            // pass read as a stack of unrelated blocks.
            className="flex w-full flex-col gap-10 self-start pb-24 pt-8"
            aria-busy="true"
            aria-label="Loading conversation"
            data-testid="chat-transcript-skeleton"
        >
            {GHOST_TURNS.map((turn, i) => (
                <div key={i} className="flex w-full flex-col gap-4">
                    <div className="flex w-full justify-end">
                        <div className={cn('forge-skeleton h-9 rounded-2xl rounded-tr-md', turn.user)} />
                    </div>

                    <div className="w-full">
                        <div className="mb-2.5 flex items-center gap-2">
                            <div className="forge-skeleton h-5 w-5 rounded-md" />
                            <div className="forge-skeleton h-2.5 w-20 rounded-full" />
                        </div>
                        <div className="space-y-2.5 border-l-2 border-primary/15 pl-4 md:pl-5">
                            {turn.lines.map((w, j) => (
                                <div key={j} className={cn('forge-skeleton h-3 rounded-full', w)} />
                            ))}
                        </div>
                    </div>
                </div>
            ))}
        </motion.div>
    );
}

export default ChatTranscriptSkeleton;
